import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, Send, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import Header from '@/components/common/Header';
import SEOHead from '@/components/common/SEOHead';
import FooterSection from '@/components/homepage/FooterSection';
import { useAnalytics } from '@/hooks/useAnalytics';
import { sendContactEmail, type ContactFormData } from '@/utils/contactService';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { trackContactForm, trackNavigation } = useAnalytics(); // This also handles page view tracking

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send the actual email
      const result = await sendContactEmail(formData as ContactFormData);

      if (result.success) {
        // Track successful form submission
        trackContactForm('contact_page', true);
        
        toast({
          title: "Message sent!",
          description: result.message || "Thank you for your message. We'll get back to you soon.",
        });
        
        // Clear form on success
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        // Track failed form submission
        trackContactForm('contact_page', false);
        
        toast({
          title: "Failed to send message",
          description: result.error || "Something went wrong. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      // Track failed form submission
      trackContactForm('contact_page', false);
      
      console.error('Contact form error:', error);
      toast({
        title: "Failed to send message",
        description: "Something went wrong. Please try again or contact us directly at hello@appwabisabi.com",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7F5F2] to-[#F0EBE8] font-nunito">
      <SEOHead pageKey="contact" />
      <Header showActions={false} />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#6C4F77] to-[#5A3A68] text-white">
        {/* Geometric shapes */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full -translate-x-16 -translate-y-16"></div>
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-[#F4978E]/20 rotate-12 translate-x-20 translate-y-20"></div>
        
        <div className="relative z-10 section-container section-padding">
          {/* Back Navigation */}
          <div className="mb-8 flex justify-start">
            <Link to="/" onClick={() => trackNavigation('home', 'contact_page_back_button')}>
              <Button 
                variant="ghost" 
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>

          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in text-white drop-shadow-lg">
              Get in Touch
            </h1>
            <p className="text-xl text-white/95 max-w-2xl mx-auto animate-fade-in drop-shadow-sm" style={{ animationDelay: '0.1s' }}>
              We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding relative overflow-hidden">
        {/* Geometric shapes */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-[#6C4F77]/5 rounded-full -translate-x-16 -translate-y-16"></div>
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-[#FFE28C]/6 rotate-12 translate-x-20 translate-y-20"></div>
        
        <div className="section-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="shadow-lg animate-fade-in">
              <CardHeader>
                <CardTitle className="text-2xl text-[#6C4F77]">Send us a message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-[#6C4F77] font-medium">Name</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Your name"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#6C4F77] font-medium">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your@email.com"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="subject" className="text-[#6C4F77] font-medium">Subject</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      placeholder="What's this about?"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="message" className="text-[#6C4F77] font-medium">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder="Tell us how we can help you..."
                      rows={5}
                      className="mt-1"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-[#6C4F77] hover:bg-[#5A3A68]"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div>
                <h2 className="text-3xl font-bold text-[#6C4F77] mb-6">Let's Connect</h2>
                <p className="text-[#2C2C2C] text-lg leading-relaxed mb-8">
                  Whether you have questions about Wabisabi, need support, or just want to share your thoughts, 
                  we're here to listen. Your feedback helps us build better connections.
                </p>
              </div>

              <div className="space-y-6">
                <Card className="border-[#6C4F77]/20 hover:border-[#6C4F77]/40 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#6C4F77]/10 rounded-lg flex items-center justify-center mr-4">
                        <Mail className="h-6 w-6 text-[#6C4F77]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#6C4F77] mb-1">Email</h3>
                        <a href="mailto:hello@appwabisabi.com" className="text-[#6C4F77] hover:underline">hello@appwabisabi.com</a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-gradient-to-br from-[#6C4F77]/5 to-[#F4978E]/5 rounded-xl p-6 border border-[#6C4F77]/10">
                <h3 className="font-semibold text-[#6C4F77] mb-3">Response Time</h3>
                <p className="text-[#2C2C2C]">
                  We typically respond within 24 hours during business days. For urgent matters, 
                  please mention it in your subject line.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default Contact;